import React from 'react';
import { PortalContext } from '../../context/portal-context';
import { GetPersonUser, PersonBenefits } from '@esportal/portal-service';
import { DataVehicles } from '@esportal/portal-service';
import { StyledSpinner } from '../styled-spinner/styled-spinner';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import styled from 'styled-components';
import {
	Container,
	ContainerPadding,
	Progress,
	TextAlignment,
	TokenTextAppearance,
	Text,
} from '@volkswagen-onehub/components-core';

export const LoadingManager = ({
	userVehicles,
	userData,
	personBenefits,
	hasError,
}: {
	userVehicles: DataVehicles | null;
	userData: GetPersonUser | null;
	personBenefits?: PersonBenefits | null;
	hasError: boolean;
}): React.ReactElement => {
	const { portalService } = React.useContext(PortalContext);

	const trackingManager = useTrackingManager();
	const [validatingIDKit, setValidatingIDKit] = React.useState<boolean>(true);
	const [progressValue, setProgressValue] = React.useState<number>(0.15);
	const [showAlmostThere, setShowAlmostThere] = React.useState<boolean>(false);

	React.useEffect(() => {
		const subscription = portalService.isValidatingIDKit().subscribe(setValidatingIDKit);
		return () => subscription.unsubscribe();
	}, [portalService]);

	React.useEffect(() => {
		let target = progressValue;

		if (!validatingIDKit) {
			target = 0.5;
		}
		if (userData && !userVehicles) {
			target = 0.75;
		}
		if (userData && userVehicles) {
			target = 0.9;
		}
		if (userData && userVehicles && personBenefits) {
			target = 1;
		}

		const step = () => {
			setProgressValue((prev) => {
				const diff = target - prev;
				const next = Math.abs(diff) < 0.01 ? target : prev + diff * 0.1;

				if (next >= 0.75 && !showAlmostThere) {
					setShowAlmostThere(true);
				}

				return next;
			});
		};

		const interval = setInterval(step, 1000 / 60); // 60fps
		return () => clearInterval(interval);
	}, [validatingIDKit, userData, userVehicles, personBenefits, showAlmostThere]);

	React.useEffect(() => {
		if (hasError) {
			let errorType = '';

			if (!userVehicles && userData && !validatingIDKit) {
				errorType = 'vehicles';
			} else if (!userData && !validatingIDKit) {
				errorType = 'person';
			} else if (!validatingIDKit) {
				errorType = 'user';
			}
			trackingManager.trackClubVWErrorMessageLoad(
				{
					message: 'Uy, algo no está funcionando.',
				},
				{
					contentId: `Error: AuthProxy - ${errorType}`,
				},
			);
		}
	}, [hasError]);

	const StyledErrorContainer = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 70vh;
		width: 100%;

		@media (min-width: 375px) {
			padding-left: var(--size-grid002);
			padding-right: var(--size-grid002);
		}
		@media (min-width: 560px) {
			padding-left: var(--size-grid004);
			padding-right: var(--size-grid004);
		}

		@media (min-width: 960px) {
			padding-left: var(--size-grid006);
			padding-right: var(--size-grid006);
		}

		@media (min-width: 1280px) {
			padding-left: var(--size-grid007);
			padding-right: var(--size-grid007);
		}

		@media (min-width: 1600px) {
			padding-left: var(--size-grid008);
			padding-right: var(--size-grid008);
		}

		@media (min-width: 1920px) {
			padding-left: var(--size-grid008);
			padding-right: var(--size-grid008);
		}
	`;
	const StyledProgressContainer = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 70vh;
		width: 100%;

		@media (min-width: 375px) {
			padding-left: var(--size-grid002);
			padding-right: var(--size-grid002);
		}
		@media (min-width: 560px) {
			padding-left: var(--size-grid004);
			padding-right: var(--size-grid004);
		}

		@media (min-width: 960px) {
			padding-left: var(--size-grid006);
			padding-right: var(--size-grid006);
		}

		@media (min-width: 1280px) {
			padding-left: var(--size-grid007);
			padding-right: var(--size-grid007);
		}

		@media (min-width: 1600px) {
			padding-left: var(--size-grid008);
			padding-right: var(--size-grid008);
		}

		@media (min-width: 1920px) {
			padding-left: var(--size-grid008);
			padding-right: var(--size-grid008);
		}
	`;

	const StyledProgress = styled(Progress)`
		width: 100%;
	`;

	const StyledSVGContainer = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		padding-bottom: var(--size-dynamic0200);

		svg {
			display: block;
			margin: 0 auto;
			height: auto;
			@media (min-width: 375px) {
				width: 187px;
			}
			@media (min-width: 560px) {
				width: 187px;
			}

			@media (min-width: 960px) {
				width: 240px;
			}

			@media (min-width: 1280px) {
				width: 240px;
			}

			@media (min-width: 1600px) {
				width: 266px;
			}

			@media (min-width: 1920px) {
				width: 320px;
			}
		}
	`;

	const Message = styled.div`
		position: absolute;
		top: calc(50% + 40px);
		left: 50%;
		transform: translateX(-50%);
	`;

	const isLoading = validatingIDKit || !userData || !userVehicles || !personBenefits;

	return (
		<>
			{hasError ? (
				<StyledErrorContainer>
					<StyledSVGContainer>
						<svg viewBox="0 0 321 160" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M60.6566 0C62.0087 0 63.4521 0.245202 64.7125 0.821545C65.9566 1.39049 67.2922 2.42138 67.829 4.11579L67.8339 4.12867L67.8371 4.13995L107.947 135.257H114.7C118.168 135.257 120.98 138.065 120.98 141.527V153.732C120.98 157.193 118.168 160 114.7 160H6.61294C3.14503 160 0.333008 157.193 0.333008 153.732V141.527C0.333008 138.065 3.14503 135.257 6.61294 135.257H13.3658L53.4761 4.13995L53.4792 4.12867L53.4842 4.11579C54.021 2.42138 55.3565 1.39049 56.6007 0.821545C57.861 0.245202 59.3045 0 60.6566 0ZM106.106 140.21H15.2073V140.178C15.0808 140.195 14.9538 140.206 14.8264 140.206H6.61294C5.88331 140.206 5.29111 140.798 5.29111 141.527V153.732C5.29111 154.46 5.88331 155.051 6.61294 155.051H114.7C115.43 155.051 116.022 154.46 116.022 153.732V141.527C116.022 140.798 115.43 140.206 114.7 140.206H106.487C106.358 140.206 106.231 140.194 106.106 140.178V140.21ZM90.4794 114.137C70.6475 116.126 50.6657 116.126 30.8338 114.137L25.1832 113.57L18.5482 135.262H102.765L96.1283 113.57L90.4794 114.137ZM60.6566 4.9486C59.856 4.9486 59.1467 5.10122 58.6666 5.32072C58.3296 5.47479 58.2255 5.59971 58.2033 5.63161L26.6584 108.744L31.3292 109.212C50.8318 111.168 70.4814 111.168 89.9839 109.212L94.6531 108.744L63.1082 5.63161C63.0851 5.59853 62.9809 5.47358 62.6466 5.32072C62.1665 5.10122 61.4571 4.9486 60.6566 4.9486ZM52.3818 89.0748C59.3938 89.043 64.6533 90.3407 68.2083 91.6877C69.9829 92.36 71.3274 93.0421 72.2481 93.5724C72.7084 93.8375 73.0637 94.0657 73.3133 94.2344C73.4377 94.3185 73.5362 94.388 73.6086 94.4406C73.6448 94.4668 73.6742 94.4893 73.6974 94.5067C73.7089 94.5155 73.7197 94.5229 73.7281 94.5292C73.7323 94.5325 73.7361 94.5363 73.7394 94.5389L73.7442 94.5422L73.7458 94.5437C73.7458 94.5437 73.7478 94.545 72.5354 96.0999L73.7491 94.5453C74.829 95.3848 75.0225 96.9403 74.1816 98.0184C73.3427 99.0934 71.7915 99.2881 70.7116 98.4566L70.7148 98.4598C70.7148 98.4598 70.704 98.4518 70.6906 98.442C70.6622 98.4214 70.6096 98.3831 70.5325 98.3309C70.3773 98.226 70.1236 98.0623 69.7706 97.8589C69.0642 97.452 67.9592 96.8862 66.4491 96.3141C63.4344 95.1718 58.7778 93.9946 52.4043 94.0234C51.0352 94.0297 49.9203 92.927 49.914 91.5603C49.9081 90.1942 51.0129 89.0811 52.3818 89.0748ZM42.4349 72.5795C43.8012 72.5564 44.9285 73.6413 44.9559 75.0038V75.0071C44.9569 75.0276 44.9591 75.0685 44.964 75.1262C44.9741 75.2437 44.9949 75.4271 45.0351 75.6531C45.1174 76.1168 45.2716 76.6979 45.5466 77.2495C45.8185 77.7943 46.1791 78.2542 46.6554 78.58C47.1114 78.8916 47.8126 79.1776 48.9472 79.1776C51.0317 79.1775 51.9705 78.2014 52.5255 77.1769C52.8314 76.6118 53.0133 76.0199 53.1161 75.5531C53.1662 75.3256 53.1945 75.1414 53.2097 75.0231L53.2242 74.9024C53.3078 73.5405 54.4803 72.5034 55.8454 72.5843C57.2119 72.6656 58.2539 73.8364 58.1727 75.2004L55.6985 75.0538C58.1731 75.2009 58.1727 75.2036 58.1727 75.2036V75.2116C58.1724 75.2156 58.1714 75.2199 58.1711 75.2245C58.1704 75.2341 58.1704 75.2456 58.1694 75.2583C58.1676 75.2839 58.1648 75.3156 58.1613 75.3533C58.1544 75.4294 58.1432 75.53 58.1275 75.6514C58.0964 75.8941 58.044 76.226 57.958 76.6164C57.7883 77.3866 57.475 78.4447 56.8879 79.5288C55.6565 81.803 53.2198 84.1261 48.9472 84.1262C46.9033 84.1262 45.2053 83.5873 43.8519 82.6619C42.5197 81.7507 41.6591 80.5608 41.1083 79.4562C40.5607 78.3586 40.2902 77.2897 40.1528 76.5164C40.0831 76.1242 40.0446 75.7918 40.0236 75.5483C40.0132 75.4263 40.0081 75.325 40.0043 75.2488C40.0025 75.211 40.0003 75.1792 39.9995 75.1536C39.999 75.1411 39.9996 75.1293 39.9995 75.1198C39.9993 75.1153 39.9978 75.1107 39.9978 75.1069V75.0988C39.9978 75.0988 39.9982 75.0959 42.4769 75.0538L39.9978 75.0957C39.9747 73.7294 41.066 72.6027 42.4349 72.5795ZM67.2254 72.5795C68.5917 72.5564 69.719 73.6413 69.7464 75.0038V75.0071C69.7474 75.0276 69.7496 75.0685 69.7545 75.1262C69.7646 75.2437 69.7854 75.4271 69.8256 75.6531C69.9079 76.1168 70.0621 76.6979 70.3371 77.2495C70.609 77.7943 70.9696 78.2542 71.4459 78.58C71.9019 78.8916 72.6031 79.1776 73.7377 79.1776C75.8223 79.1775 76.761 78.2014 77.316 77.1769C77.6219 76.6118 77.8038 76.0199 77.9066 75.5531C77.9567 75.3256 77.985 75.1414 78.0002 75.0231L78.0147 74.9024C78.0984 73.5405 79.2708 72.5034 80.6359 72.5843C82.0024 72.6656 83.0444 73.8364 82.9632 75.2004L80.489 75.0538C82.9636 75.2009 82.9632 75.2036 82.9632 75.2036V75.2116C82.9629 75.2156 82.9619 75.2199 82.9616 75.2245C82.9609 75.2341 82.9609 75.2456 82.9599 75.2583C82.9581 75.2839 82.9553 75.3156 82.9518 75.3533C82.9449 75.4294 82.9337 75.53 82.918 75.6514C82.8869 75.8941 82.8345 76.226 82.7486 76.6164C82.5788 77.3866 82.2655 78.4447 81.6784 79.5288C80.447 81.803 78.0103 84.1261 73.7377 84.1262C71.6938 84.1262 69.9958 83.5873 68.6424 82.6619C67.3102 81.7507 66.4496 80.5608 65.8988 79.4562C65.3512 78.3586 65.0807 77.2897 64.9434 76.5164C64.8736 76.1242 64.8351 75.7918 64.8141 75.5483C64.8037 75.4263 64.7986 75.325 64.7948 75.2488C64.793 75.211 64.7908 75.1792 64.79 75.1536C64.7895 75.1411 64.7902 75.1293 64.79 75.1198C64.7898 75.1153 64.7883 75.1107 64.7883 75.1069V75.0988C64.7883 75.0988 64.7887 75.0959 67.2674 75.0538L64.7883 75.0957C64.7652 73.7294 65.8565 72.6027 67.2254 72.5795Z"
								fill="#0040C5"
							/>
							<path
								d="M319.595 154.335C319.098 155.59 318.337 156.84 317.337 157.797C316.349 158.742 314.898 159.603 313.121 159.477L313.108 159.477L313.097 159.476L176.185 148.451L173.7 154.718C172.424 157.937 168.773 159.513 165.548 158.239L154.178 153.748C150.953 152.474 149.373 148.832 150.649 145.613L190.423 45.3029C191.699 42.0845 195.348 40.5076 198.574 41.7812L209.945 46.2727C213.169 47.5464 214.751 51.1894 213.475 54.4078L210.989 60.6748L318.38 146.148L318.39 146.155L318.4 146.164C319.782 147.286 320.25 148.905 320.322 150.269C320.395 151.651 320.092 153.081 319.595 154.335ZM205.727 60.5764C205.759 60.4519 205.795 60.3287 205.841 60.2094L208.864 52.5869C209.132 51.9096 208.798 51.1421 208.12 50.8741L196.75 46.3825C196.071 46.1146 195.302 46.4467 195.034 47.1238L155.259 147.434C154.991 148.111 155.324 148.878 156.002 149.147L167.373 153.638C168.051 153.906 168.821 153.574 169.09 152.897L172.113 145.275C172.159 145.156 172.217 145.044 172.277 144.934L172.249 144.923L205.698 60.5647L205.727 60.5764ZM209.079 65.4831L178.089 143.641L200.736 145.464L202.286 140.015C207.732 120.878 215.085 102.333 224.234 84.6603L226.845 79.621L209.079 65.4831ZM314.986 152.514C315.28 151.771 315.399 151.057 315.371 150.531C315.351 150.161 315.273 150.019 315.252 149.986L230.797 82.767L228.64 86.9322C219.641 104.312 212.411 122.548 207.056 141.367L205.775 145.87L313.447 154.538C313.485 154.529 313.64 154.478 313.906 154.224C314.287 153.86 314.69 153.257 314.986 152.514ZM228.351 128.935C227.849 130.206 226.408 130.829 225.135 130.326C223.862 129.823 223.238 128.385 223.742 127.115L231.04 108.709C231.544 107.439 232.984 106.816 234.258 107.319C235.53 107.822 236.153 109.259 235.649 110.53L228.351 128.935ZM255.993 120.012C256.536 121.266 255.959 122.722 254.703 123.265C253.446 123.808 251.988 123.232 251.443 121.978L249.645 117.826L245.481 119.625C244.225 120.168 242.766 119.591 242.222 118.337C241.678 117.083 242.255 115.627 243.513 115.084L247.674 113.286L245.871 109.133C245.328 107.879 245.906 106.423 247.162 105.88C248.418 105.337 249.878 105.914 250.421 107.167L252.224 111.32L256.383 109.523C257.639 108.98 259.098 109.557 259.642 110.811C260.185 112.065 259.607 113.521 258.351 114.064L254.193 115.861L255.993 120.012ZM247.023 142.635C247.567 143.889 246.989 145.346 245.733 145.889C244.476 146.432 243.017 145.855 242.473 144.601L240.674 140.45L236.512 142.248C235.256 142.791 233.797 142.214 233.251 140.961C232.708 139.707 233.286 138.25 234.542 137.707L238.704 135.909L236.902 131.756C236.358 130.502 236.935 129.046 238.191 128.503C239.447 127.96 240.907 128.537 241.45 129.791L243.253 133.943L247.412 132.147C248.668 131.604 250.127 132.18 250.671 133.434C251.215 134.688 250.638 136.145 249.382 136.687L245.224 138.484L247.023 142.635Z"
								fill="#0040C5"
							/>
						</svg>
					</StyledSVGContainer>
					<Container padding={{ bottom: ContainerPadding.static150 }}>
						<Text appearance={TokenTextAppearance.headline300}>Uy, algo no está funcionando.</Text>
					</Container>

					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center} bold>
						Por favor, inténtalo de nuevo más tarde.
					</Text>
				</StyledErrorContainer>
			) : isLoading ? (
				<StyledProgressContainer>
					<StyledProgress value={progressValue} />
					{showAlmostThere && (
						<Message>
							<Text appearance={TokenTextAppearance.headline300} bold>
								Casi lo tenemos...
							</Text>
						</Message>
					)}
				</StyledProgressContainer>
			) : (
				<StyledSpinner variant="x-large" />
			)}
		</>
	);
};
